import * as THREE from "three";
import { inked, toon } from "../toon";
import { buildEar, buildEye, eyeMaterials } from "./face-parts";
import type { SkaterLook } from "./look";
import { skinMat } from "./materials";
import { facets } from "./geom";

// The toy head (the cartoon rider's, `RiderBuild.face: "toy"`), head space,
// face +z: a round skull and jaw, big glossy eyes with catch-lights, a grin,
// cone spikes for a mohawk, and hair under headwear as simple shapes.

/**
 * The toy face; `eyes` shrinks the big toy eyes (1 is the cartoon's).
 * Face looks toward the nose of the board (+z), like a skater watching the line.
 */
export function buildToyFace(look: SkaterLook, into: THREE.Group, eyes: number): void {
  const skin = skinMat(look.skin);
  const head = inked(new THREE.SphereGeometry(0.2, facets(32), facets(24, 2)), skin, 0.02);
  head.scale.set(1, 1.05, 1);
  into.add(head);
  // A rounder jaw and cheeks under the skull.
  const jaw = new THREE.Mesh(new THREE.SphereGeometry(0.16, facets(24), facets(16, 2)), skin);
  jaw.name = "jaw";
  jaw.scale.set(1.05, 0.8, 0.95);
  jaw.position.set(0, -0.07, 0.03);
  into.add(jaw);
  // Big toy eyes: glossy whites, a colored iris, a pupil and a catch-light.
  const mats = eyeMaterials(look);
  const glint = new THREE.MeshBasicMaterial({ color: 0xffffff });
  for (const side of [-1, 1]) {
    const eye = buildEye(side, eyes, mats);
    const g = new THREE.Mesh(new THREE.SphereGeometry(0.007, facets(8), facets(6, 2)), glint);
    g.name = "glint";
    g.position.set(side * 0.004 + 0.009, 0.01, 0.042);
    eye.add(g);
    into.add(eye);
    // Eyebrows, tilted in a little for a determined look.
    const b = new THREE.Mesh(new THREE.CapsuleGeometry(0.011, 0.045, facets(3, 1), facets(8)), mats.brow);
    b.name = "brow";
    b.rotation.z = Math.PI / 2 + side * 0.25;
    b.position.set(side * 0.078, 0.115, 0.172);
    if (eyes !== 1) b.scale.setScalar((1 + eyes) / 2);
    into.add(b);
    into.add(buildEar(side, skin));
  }
  const nose = new THREE.Mesh(new THREE.SphereGeometry(0.03, facets(14), facets(10, 2)), skin);
  nose.scale.set(1, 0.9, 1.1);
  nose.position.set(0, -0.02, 0.2);
  into.add(nose);
  // A lopsided grin; a look's own lips (#239) soften it halfway toward them,
  // still dark enough to read as a line on the skin.
  const smile = new THREE.Mesh(
    new THREE.TorusGeometry(0.05, 0.009, facets(6, 2), facets(16), Math.PI * 0.8),
    new THREE.MeshBasicMaterial({ color: toyGrin(look) }),
  );
  smile.name = "smile";
  smile.rotation.z = Math.PI + Math.PI * 0.1 + 0.15;
  smile.position.set(0.01, -0.06, 0.185);
  smile.rotation.x = -0.25;
  into.add(smile);
}

const GRIN = 0x5a1f2e;

/** The toy face's grin color: dark plum, or halfway from it to the look's own lips. */
export function toyGrin(look: SkaterLook): THREE.Color {
  const grin = new THREE.Color(GRIN);
  return look.lips ? grin.lerp(new THREE.Color(look.lips), 0.5) : grin;
}

/** A row of spikes from the forehead over the crown to the back of the head (head space, face +z). */
export function buildMohawk(color: string): THREE.Group {
  const mohawk = new THREE.Group();
  mohawk.name = "mohawk";
  const mat = toon(color, { roughness: 0.6 }, "hair");
  const R = 0.2;
  const COUNT = 7;
  for (let i = 0; i < COUNT; i++) {
    const u = i / (COUNT - 1);
    // Front (+z, tilted forward) to back; tallest just behind the crown.
    const a = 0.95 - u * 2.05;
    const h = 0.13 + 0.09 * Math.sin(Math.PI * Math.min(1, u * 1.15));
    const geo = new THREE.ConeGeometry(0.05, h, facets(8));
    geo.translate(0, h / 2, 0);
    const spike = inked(geo, mat, 0.012);
    spike.name = "spike";
    spike.position.set(0, R * 1.05 * Math.cos(a) - 0.02, R * Math.sin(a));
    // Point out from the skull, swept back a little.
    spike.rotation.x = a - 0.25;
    spike.scale.set(0.55, 1, 1);
    mohawk.add(spike);
  }
  return mohawk;
}

/** Dark sideburns under the cowboy hat. */
export function buildToyHatHair(color: string): THREE.Object3D[] {
  const hairMat = toon(color, { roughness: 0.7 }, "hair");
  return [-1, 1].map((side) => {
    const burn = new THREE.Mesh(new THREE.CapsuleGeometry(0.025, 0.06, facets(3, 1), facets(8)), hairMat);
    burn.name = "sideburn";
    burn.position.set(side * 0.19, 0.04, 0.03);
    return burn;
  });
}

/** A fringe of hair in the space helmet's opening and a ponytail out the back. */
export function buildToyHelmetHair(color: string): THREE.Object3D[] {
  const hairMat = toon(color, { roughness: 0.7 }, "hair");
  const fringe = new THREE.Mesh(new THREE.SphereGeometry(0.2, facets(20), facets(8, 2), 0, Math.PI * 2, 0, Math.PI * 0.32), hairMat);
  fringe.name = "fringe";
  fringe.scale.set(1.02, 1, 1.04);
  const tail = inked(new THREE.CapsuleGeometry(0.045, 0.16, facets(4, 1), facets(10)), hairMat, 0.01);
  tail.name = "ponytail";
  tail.position.set(0, -0.1, -0.29);
  tail.rotation.x = 0.5;
  return [fringe, tail];
}
